import React, { useState, useEffect } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { checkPassword, checkIsEmail, checkKoachHandler } from "helpers/Reg.helper";
import { checkExistsUsername } from "api/common/Auth.api";
import { checkExistsNicknameOfKoach, checkExistsHandlerOfKoach } from "api/common/Verification.api";

interface KoachSignupStep1Props {
    accountParams: {
        username: string;
        password: string;
        confirmedPassword: string;
        nickname: string;
        handler: string;
    };
    handleInputChange: (section: string, field: string, value: any) => void;
    onValidationChange?: (isValid: boolean) => void;
}

interface ValidationErrors {
    username: string;
    password: string;
    confirmedPassword: string;
    nickname: string;
    handler: string;
}

const KoachSignupStep1: React.FC<KoachSignupStep1Props> = ({
    accountParams,
    handleInputChange,
    onValidationChange
}) => {
    const [errors, setErrors] = useState<ValidationErrors>({
        username: "",
        password: "",
        confirmedPassword: "",
        nickname: "",
        handler: ""
    });

    const [isChecking, setIsChecking] = useState({
        username: false,
        nickname: false,
        handler: false
    });

    // 모든 필드가 입력되었는지 확인
    const isAllFieldsFilled = () => {
        return (
            accountParams.username.trim() !== "" &&
            accountParams.password.trim() !== "" &&
            accountParams.confirmedPassword.trim() !== "" &&
            accountParams.nickname.trim() !== "" &&
            accountParams.handler.trim() !== ""
        );
    };

    // 모든 유효성 검사가 통과되었는지 확인
    const isAllValidationsPassed = () => {
        return (
            Object.values(errors).every(error => error === "") &&
            !isChecking.username &&
            !isChecking.nickname &&
            !isChecking.handler
        );
    };

    // 전체 유효성 검사 상태를 부모 컴포넌트에 전달
    useEffect(() => {
        const isValid = isAllFieldsFilled() && isAllValidationsPassed();
        onValidationChange?.(isValid);
    }, [errors, isChecking, accountParams]);

    // 이메일 중복 확인
    const checkEmailDuplicate = async (email: string) => {
        if (!email) return;

        setIsChecking(prev => ({ ...prev, username: true }));
        try {
            if (!checkIsEmail(email)) {
                setErrors(prev => ({ ...prev, username: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtIncorrectEmail") }));
                return;
            }

            const response = await checkExistsUsername(email, "accountType:koach", "signupType:email");
            if (response?.data?.data?.exists) {
                setErrors(prev => ({ ...prev, username: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtExistingEmail") }));
            } else {
                setErrors(prev => ({ ...prev, username: "" }));
            }
        } catch (error) {
            setErrors(prev => ({ ...prev, username: getLanguageValue(PAGE_TYPE.COMMON, "error.txtErrorEmail") }));
        } finally {
            setIsChecking(prev => ({ ...prev, username: false }));
        }
    };

    // 닉네임 길이 검사
    const validateNicknameLength = (nickname: string) => {
        if (nickname.length < 4) {
            setErrors(prev => ({ ...prev, nickname: getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.nicknameErrorTooShort") }));
            return false;
        }
        if (nickname.length > 10) {
            setErrors(prev => ({ ...prev, nickname: getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.nicknameErrorTooLong") }));
            return false;
        }
        return true;
    };

    // 닉네임 중복 확인
    const checkNicknameDuplicate = async (nickname: string) => {
        if (!nickname) return;

        setIsChecking(prev => ({ ...prev, nickname: true }));
        try {
            const response = await checkExistsNicknameOfKoach(nickname);
            if (response?.data?.data?.exists) {
                setErrors(prev => ({ ...prev, nickname: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtExistingNickname") }));
            } else {
                setErrors(prev => ({ ...prev, nickname: "" }));
            }
        } catch (error) {
            setErrors(prev => ({ ...prev, nickname: getLanguageValue(PAGE_TYPE.COMMON, "error.txtUnknownError") }));
        } finally {
            setIsChecking(prev => ({ ...prev, nickname: false }));
        }
    };

    // 핸들러 길이 검사
    const validateHandlerLength = (handler: string) => {
        if (handler.length < 4) {
            setErrors(prev => ({ ...prev, handler: getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.handlerErrorTooShort") }));
            return false;
        }
        if (handler.length > 10) {
            setErrors(prev => ({ ...prev, handler: getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.handlerErrorTooLong") }));
            return false;
        }
        return true;
    };

    // 핸들러 중복 확인
    const checkHandlerDuplicate = async (handler: string) => {
        if (!handler) return;

        setIsChecking(prev => ({ ...prev, handler: true }));
        try {
            if (!checkKoachHandler(handler)) {
                setErrors(prev => ({ ...prev, handler: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtIncorrectHandler") }));
                return;
            }

            const response = await checkExistsHandlerOfKoach(handler);
            if (response?.data?.data?.exists) {
                setErrors(prev => ({ ...prev, handler: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtExistingHandler") }));
            } else {
                setErrors(prev => ({ ...prev, handler: "" }));
            }
        } catch (error) {
            setErrors(prev => ({ ...prev, handler: getLanguageValue(PAGE_TYPE.COMMON, "error.txtUnknownError") }));
        } finally {
            setIsChecking(prev => ({ ...prev, handler: false }));
        }
    };

    // 비밀번호 유효성 검사
    const validatePassword = (password: string) => {
        if (!password) {
            setErrors(prev => ({ ...prev, password: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtNoPassword") }));
            return;
        }

        if (!checkPassword(password)) {
            setErrors(prev => ({ ...prev, password: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtIncorrectPassword") }));
        } else {
            setErrors(prev => ({ ...prev, password: "" }));
        }
    };

    // 비밀번호 확인 검사
    const validateConfirmedPassword = (confirmedPassword: string) => {
        if (!confirmedPassword) {
            setErrors(prev => ({ ...prev, confirmedPassword: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtNoConfirmedPassword") }));
            return;
        }

        if (confirmedPassword !== accountParams.password) {
            setErrors(prev => ({ ...prev, confirmedPassword: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtPasswordMismatch") }));
        } else {
            setErrors(prev => ({ ...prev, confirmedPassword: "" }));
        }
    };

    // 입력 필드 변경 핸들러
    const handleFieldChange = (field: string, value: string) => {
        handleInputChange("account", field, value);

        // 실시간 유효성 검사
        switch (field) {
            case "password":
                validatePassword(value);
                // 비밀번호가 변경되면 비밀번호 확인도 다시 검사
                if (accountParams.confirmedPassword) {
                    validateConfirmedPassword(accountParams.confirmedPassword);
                }
                break;
            case "confirmedPassword":
                validateConfirmedPassword(value);
                break;
            case "nickname":
                // 닉네임이 비어있지 않을 때만 길이 검사
                if (value && !validateNicknameLength(value)) {
                    // 길이 검사에서 실패하면 에러 메시지는 이미 설정됨
                    return;
                } else if (value && value.length >= 4 && value.length <= 10) {
                    // 길이가 유효하면 에러 메시지 제거
                    setErrors(prev => ({ ...prev, nickname: "" }));
                }
                break;
            case "handler":
                // 핸들러가 비어있지 않을 때만 길이 검사
                if (value && !validateHandlerLength(value)) {
                    // 길이 검사에서 실패하면 에러 메시지는 이미 설정됨
                    return;
                } else if (value && value.length >= 4 && value.length <= 10) {
                    // 길이가 유효하면 에러 메시지 제거
                    setErrors(prev => ({ ...prev, handler: "" }));
                }
                break;
        }
    };

    // blur 이벤트 핸들러
    const handleBlur = (field: string, value: string) => {
        switch (field) {
            case "username":
                if (!value) {
                    setErrors(prev => ({ ...prev, username: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtNoEmail") }));
                } else if (!checkIsEmail(value)) {
                    setErrors(prev => ({ ...prev, username: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtIncorrectEmail") }));
                } else {
                    checkEmailDuplicate(value);
                }
                break;
            case "password":
                validatePassword(value);
                break;
            case "confirmedPassword":
                validateConfirmedPassword(value);
                break;
            case "nickname":
                if (!value) {
                    setErrors(prev => ({ ...prev, nickname: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtNoNickname") }));
                } else if (!validateNicknameLength(value)) {
                    // 길이 검사에서 실패하면 중복 확인은 하지 않음
                    return;
                } else {
                    checkNicknameDuplicate(value);
                }
                break;
            case "handler":
                if (!value) {
                    setErrors(prev => ({ ...prev, handler: getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.handlerErrorNoInput") }));
                } else if (!validateHandlerLength(value)) {
                    // 길이 검사에서 실패하면 중복 확인은 하지 않음
                    return;
                } else {
                    checkHandlerDuplicate(value);
                }
                break;
        }
    };

    const getInputClassName = (field: keyof ValidationErrors) => {
        const baseClass = "block w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base transition-colors";
        return errors[field]
            ? `${baseClass} border-[#CF3688]`
            : `${baseClass} border-gray-200`;
    };

    return (
        <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
            <div className="p-6">
                <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                        {getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.title")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.desc")}
                    </p>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span>
                            {getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.email")}
                        </label>
                        <div className="relative">
                            <input
                                type="email"
                                value={accountParams.username}
                                onChange={(evt) => handleFieldChange("username", evt.target.value)}
                                onBlur={(evt) => handleBlur("username", evt.target.value)}
                                required
                                className={getInputClassName("username")}
                                placeholder={getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.emailPlaceholder")}
                            />
                            {isChecking.username && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#CF3688]"></div>
                                </div>
                            )}
                        </div>
                        {errors.username && (
                            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm text-red-600">{errors.username}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span>
                            {getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.password")}
                        </label>
                        <input
                            type="password"
                            value={accountParams.password}
                            onChange={(evt) => handleFieldChange("password", evt.target.value)}
                            onBlur={(evt) => handleBlur("password", evt.target.value)}
                            required
                            className={getInputClassName("password")}
                            placeholder={getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.passwordPlaceholder")}
                            minLength={8}
                            maxLength={16}
                        />
                        {errors.password && (
                            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm text-red-600">{errors.password}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span>
                            {getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.confirmedPassword")}
                        </label>
                        <input
                            type="password"
                            value={accountParams.confirmedPassword}
                            onChange={(evt) => handleFieldChange("confirmedPassword", evt.target.value)}
                            onBlur={(evt) => handleBlur("confirmedPassword", evt.target.value)}
                            required
                            className={getInputClassName("confirmedPassword")}
                            placeholder={getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.confirmedPasswordPlaceholder")}
                            minLength={8}
                            maxLength={16}
                        />
                        {errors.confirmedPassword && (
                            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm text-red-600">{errors.confirmedPassword}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                                <span className="text-red-500">*</span>
                                <span>{getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.nickname")}</span>
                            </div>
                            <em className="text-xs text-gray-500 ml-1">
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.nicknameDesc")}
                            </em>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={accountParams.nickname}
                                onChange={(evt) => handleFieldChange("nickname", evt.target.value)}
                                onBlur={(evt) => handleBlur("nickname", evt.target.value)}
                                required
                                className={getInputClassName("nickname")}
                                placeholder={getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.nicknamePlaceholder")}
                                minLength={4}
                                maxLength={10}
                            />
                            {isChecking.nickname && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#CF3688]"></div>
                                </div>
                            )}
                        </div>
                        {errors.nickname && (
                            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm text-red-600">{errors.nickname}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                                <span className="text-red-500">*</span>
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.handler")}
                            </div>
                            <em className="text-xs text-gray-500">
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.handlerDesc")} <br />
                                https://wannabes.app/info/koach/<u>{accountParams.handler}</u>
                            </em>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={accountParams.handler}
                                onChange={(evt) => handleFieldChange("handler", evt.target.value.toLowerCase())}
                                onBlur={(evt) => handleBlur("handler", evt.target.value)}
                                required
                                className={getInputClassName("handler")}
                                placeholder={getLanguageValue(PAGE_TYPE.KOACH, "signupStep1.handlerPlaceholder")}
                                minLength={4}
                                maxLength={10}
                            />
                            {isChecking.handler && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#CF3688]"></div>
                                </div>
                            )}
                        </div>
                        {errors.handler && (
                            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm text-red-600">{errors.handler}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default KoachSignupStep1; 